import { request } from '@umijs/max';
import { ADMIN_COMPANY_PREFIX } from '@/utils/adminApi';

type ApiResponse<T> = {
  status: number;
  error_code?: number;
  error_msg?: string;
  result_body?: T;
};

export type RegionItem = {
  id: string;
  companyId: string;
  miniProgramId: string;
  miniProgramName: string;
  name: string;
  remark?: string;
  enabled: boolean;
  deviceIds: string[];
  adminUserIds: string[];
  deviceCount: number;
  adminCount: number;
  createdAt: string;
  updatedAt: string;
};

type BackendRegion = {
  id: string | number;
  company_id: string | number;
  mini_program_id: string | number;
  mini_program_name?: string;
  name: string;
  remark?: string;
  enabled: boolean;
  device_ids?: Array<string | number>;
  admin_user_ids?: Array<string | number>;
  device_count?: number;
  admin_count?: number;
  created_at: string;
  updated_at: string;
};

const unwrap = <T>(response: ApiResponse<T>): T => {
  if (response.status !== 1 || response.result_body === undefined) {
    throw new Error(response.error_msg || '区域管理请求失败');
  }
  return response.result_body;
};

const normalizeRegion = (item: BackendRegion): RegionItem => ({
  id: String(item.id),
  companyId: String(item.company_id),
  miniProgramId: String(item.mini_program_id),
  miniProgramName: item.mini_program_name || '',
  name: item.name,
  remark: item.remark,
  enabled: item.enabled,
  deviceIds: (item.device_ids || []).map(String),
  adminUserIds: (item.admin_user_ids || []).map(String),
  deviceCount: item.device_count || 0,
  adminCount: item.admin_count || 0,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
});

export async function listRegions(params: {
  current?: number;
  pageSize?: number;
  name?: string;
  enabled?: boolean;
}) {
  const result = unwrap(
    await request<ApiResponse<{ list: BackendRegion[]; total: number }>>(
      `${ADMIN_COMPANY_PREFIX}/regions`,
      {
        method: 'GET',
        params: {
          page: params.current,
          limit: params.pageSize,
          name: params.name,
          enabled: params.enabled,
        },
      },
    ),
  );
  return {
    data: (result.list || []).map(normalizeRegion),
    total: result.total || 0,
    success: true,
  };
}

export async function listEnabledRegionsForSwitch(pageSize = 100) {
  const regions: RegionItem[] = [];
  let current = 1;
  let total = 0;

  do {
    const page = await listRegions({
      current,
      pageSize,
      enabled: true,
    });
    regions.push(...page.data);
    total = page.total;
    current += 1;
  } while (regions.length < total);

  return regions;
}

export async function createRegion(values: {
  name: string;
  remark?: string;
  enabled: boolean;
  deviceIds: string[];
}) {
  return unwrap(
    await request<ApiResponse<BackendRegion>>(
      `${ADMIN_COMPANY_PREFIX}/regions`,
      {
        method: 'POST',
        data: {
          name: values.name,
          remark: values.remark,
          enabled: values.enabled,
          device_ids: values.deviceIds,
        },
      },
    ),
  );
}

export async function updateRegion(values: {
  id: string;
  name: string;
  remark?: string;
  enabled: boolean;
  deviceIds: string[];
}) {
  return unwrap(
    await request<ApiResponse<BackendRegion>>(
      `${ADMIN_COMPANY_PREFIX}/regions`,
      {
        method: 'PUT',
        data: {
          id: values.id,
          name: values.name,
          remark: values.remark,
          enabled: values.enabled,
          device_ids: values.deviceIds,
        },
      },
    ),
  );
}

export async function copyRegion(sourceRegionId: string, name: string) {
  return unwrap(
    await request<ApiResponse<BackendRegion>>(
      `${ADMIN_COMPANY_PREFIX}/regions/copy`,
      {
        method: 'POST',
        data: { source_region_id: sourceRegionId, name },
      },
    ),
  );
}

export async function setRegionAdmins(regionId: string, userIds: string[]) {
  return unwrap(
    await request<ApiResponse<BackendRegion>>(
      `${ADMIN_COMPANY_PREFIX}/regions/admins`,
      {
        method: 'PUT',
        data: { region_id: regionId, user_ids: userIds },
      },
    ),
  );
}

export async function deleteRegion(id: string) {
  const response = await request<ApiResponse<{ message: string }>>(
    `${ADMIN_COMPANY_PREFIX}/regions`,
    { method: 'DELETE', params: { id } },
  );
  if (response.status !== 1) {
    throw new Error(response.error_msg || '删除区域失败');
  }
}
